<template>
  <section class="mt-4">
    <Spinner :display="guestStore.isLoading" />
    <div v-if="guest" class="flex flex-col space-y-4 md:flex-row md:space-y-0">
      <div class="mr-10">
        <div class="font-heading font-semibold text-4xl mb-4" id="guestName">
          {{ guest.first_name }} {{ guest.last_name }}
        </div>
        <div class="text-base font-label font-normal text-fg-secondary uppercase text-xs">
          Contact
        </div>
        <div class="text-base font-sans font-normal text-base">{{ guest.email }}</div>

        <div
          v-if="guestTags.length > 0"
          id="tag-container"
          class="flex items-center justify-start flex-wrap gap-2 mt-5"
        >
          <TagDefault
            v-for="tag in guestTags"
            :key="tag.id"
            :id="`tag-${tag.id}`"
            :tag="tag"
            type="long"
            selected
          />
        </div>
      </div>
      <div>
        <div
          v-for="(reservation, key) in reservations"
          :key="`reservation-${key}-${reservation.id}`"
          class="font-sans text-base bg-surface-primary mb-4 p-4 rounded border border-linear-subtle hover:cursor-pointer"
          @click="onGuestDetailRoute(reservation.performance_id, reservation.id)"
        >
          <div class="flex col-2xs items-center">
            <div class="mr-28">
              <div class="font-bold">{{ reservation.performance.show.name }}</div>
              <div class="text-xs font-normal">{{ reservation.datetime }}</div>
            </div>
            <div class="flex justify-end">
              <div class="font-normal">{{ reservation.number_of_guests }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import * as _ from 'lodash-es'
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import Spinner from '@/common/components/atoms/Spinner.vue'
import TagDefault from '@/common/components/atoms/TagDefault.vue'
import { PERFORMANCE_ROUTE_NAMES } from '@/modules/performance/constants/PerformanceRouteConstants'
import { useGuestStore } from '@/stores/guest'
import { useSettingStore } from '@/stores/setting'

import { GuestDetailedRead, GuestTagRead, ReservationGuestDetailedRead } from '@generated/types'

interface DecoratedReservation extends ReservationGuestDetailedRead {
  datetime: string
}

export default defineComponent({
  name: 'GuestDetail',
  components: {
    TagDefault,
    Spinner,
  },
  computed: {
    ...mapStores(useGuestStore, useSettingStore),
    guestId(): string {
      return this.$route.params.id as string
    },
    guest(): GuestDetailedRead | null {
      return this.guestStore.guest
    },
    reservations(): DecoratedReservation[] | null {
      if (!this.guest) return null
      return this.guest.reservations
        .map(reservation => {
          return {
            ...reservation,
            datetime: this.settingStore.dateTimeStr(reservation.performance.datetime),
          }
        })
        .sort((a, b) => {
          return (
            new Date(b.performance.datetime).getTime() - new Date(a.performance.datetime).getTime()
          )
        })
    },
    guestTags(): GuestTagRead[] {
      if (!this.guest) return []
      return _.compact(this.guest.tags?.map(this.settingStore.getGuestTag)) ?? []
    },
  },
  async mounted() {
    await this.settingStore.loading
    if (this.guestId) {
      await this.guestStore.fetchGuest(Number(this.guestId))
    }
  },
  methods: {
    onGuestDetailRoute(performanceId, reservationId) {
      this.$router.push({
        name: PERFORMANCE_ROUTE_NAMES.RESERVATION_DETAIL,
        params: { performanceId, reservationId },
      })
    },
  },
  watch: {
    '$route.params.id'() {
      if (this.guestId) {
        this.guestStore.fetchGuest(Number(this.guestId))
      }
    },
  },
})
</script>

<style></style>
